/**
 * Authentication library.
 *
 * provides helpers on things like getting access to the token, checking
 * if user is authenticated and other kind of access / auth functionality.
 */

import { User } from '~/jobiqo/models/user';
import GET_SEARCH_QUERY from '~/jobiqo/graphql/queries/searchRoute.gql';
import { ParsedUrlQuery } from 'querystring';
import {
  clearSearchUrlItems,
  URLfilter,
} from '~/jobiqo/services/searchUrlItems';
import { Facet, RouterInfo } from '~/jobiqo/models/search';
import {
  getCleanUrlPart,
  getGeoLocationUrlObjectFromGeoInput,
} from '~/jobiqo/components/Search/utils/searchHelpers';
import { SearchResponse } from '~/jobiqo/types/types';
import { setBrowserCookie } from './session';
import initApollo from '../apollo/initApollo';
import { generateQueryStringURL } from '../services/links';
import { getOriginalLink } from '~/jobiqo/utils/links';

/**
 * Generates path from the current context.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 *
 * @returns {string} Generated path.
 */
export const getCtxPath = (ctx): string => {
  return ctx.resolvedUrl;
};

/**
 * Gets the search route information.
 *
 * @param ctx
 *  The context object from nextjs that holds information about the request.
 */
export const getSearchRoute = async (ctx): Promise<User> => {
  const apolloClient = initApollo({}, ctx);

  const { data } = await apolloClient.query({
    query: GET_SEARCH_QUERY,
    variables: {
      path: getCleanUrlPart(getOriginalLink(getCtxPath(ctx), ctx.locale)),
      language: ctx.locale,
    },
  });
  return data.route;
};

/**
 * Checks if a route is set but the API doesn't know about it. In this case the
 * page should return a 404.
 *
 * @param route
 *  The route information that is received from the API.
 *
 * @param query
 *  The current querystring.
 */
export const checkUnknownRoute = (
  route,
  query: ParsedUrlQuery,
  newQueryString,
  configCleanFacets,
) => {
  // If there is a querystring but the API doesn't know about it its a 404.
  if (
    !route?.geoLocation &&
    !route?.facets?.some((el) => el?.facetId) &&
    query?.taxonomy
  ) {
    return true;
  }

  // We check if the users are accessing any clean URLs here because
  // the backend does not know about the clean URL structure.
  // Which filters are clean and not clean is defined in the CLEAN_FACETS_ORDER (config).
  const queryWithAnyCleanUrls =
    JSON.stringify(newQueryString) !== JSON.stringify(query);

  // If the query is different to the internal querystring check if the route
  // should return a 404.
  // If the filter is marked as NOT clean URL (e.g. for company filter -> job_organization_name)
  // then a 404 is returned if users access the clean URL (/microsoft-corporation).
  // Don't apply this to locations
  // (Locations querystring don't have the field id so this would always trigger).
  if (queryWithAnyCleanUrls && !newQueryString?.geo_location) {
    // Compare the internal newQueryString to the CLEAN_FACETS_ORDER setting
    // and return terms that are not part of CLEAN_FACETS_ORDER (in config).
    // If the term is an unclean should not allow accessing as a clean url.
    // we are checking basically if a unclean term is present in the cleanTerms. It should never be.
    // .e.g user directly trying to access /jobs/microsoft.
    return !!Object.keys(newQueryString).find((term) => {
      // if the term is not just a normal querystring item (together with other clean items)
      // and its not in CLEAN_FACETS_ORDER return 404.
      return !configCleanFacets.includes(term) && !query[term];
    });
  }

  return false;
};

/**
 * Generate a querystring object from the current path.
 *
 * Geneate a nextjs compatible querystring object based
 * on the facets and geo information from the routeInfo GraphQL query. It essentially ads two things to the
 * querystring object :
 * 1 ) A "facets" key which contains information on what facets are present in the
 * current router information / url. The
 * 2 ) A "geoLocation" key which contains information on what geo location is present
 * in the current url (router info).
 *
 * This will be used to tell next js what is the router information behind a particular path
 * (e.g. /jobs/full-time/california) has the following RouteInfo value returned :
 *
 * {
 *   facets: [
 *    {
 *       "name": "field_job_employment_type",
 *       "value": "1264"
 *    }
 *  ],
 *  {
 *     "label": "California",
 *     "administrativeArea": "California",
 *     "country": null,
 *     "locationType": "administrative_area",
 *     "lat": null,
 *     "lon": null,
 *     "radius": null
 *  }
 * }
 *
 * This information is later used to make sure the graphql query includes these as conditions.
 *
 * @param {SearchResponse} routerInfo
 *  The router information received from the GraphQL query.
 */
const getRouteQuerystring = (routerInfo: SearchResponse): RouterInfo => {
  let items: RouterInfo = {
    facets: [],
  };
  if (routerInfo && routerInfo.facets) {
    routerInfo.facets
      .filter((facet) => facet && facet.facetId)
      .map((facet) => {
        if (facet && facet.facetId) {
          items.facets[facet.facetId] = facet;
        }
        return null;
      });
  }

  // Get geolocation info.
  if (routerInfo && routerInfo.geoLocation) {
    let geoLocation = {};
    Object.keys(routerInfo.geoLocation).map((geoItemKey) => {
      if (routerInfo.geoLocation) {
        geoLocation[geoItemKey] = routerInfo.geoLocation[geoItemKey];
      }
    });
    geoLocation = getGeoLocationUrlObjectFromGeoInput(geoLocation as any);
    items = { ...items, geoLocation };
  }

  return items;
};

/**
 * Handles passing important information for the client to know
 *
 * Because /full-time/enginerring means really
 * nothing to the browser and nextjs we need to make sure information about what is full-time and
 * what is enginnerring is provided and ready to use when the user ads more filters
 *
 * (@see redirectSearchWithFilter)
 */
const setCookieFilterInformation = (
  newQueryString,
  routeQuerystringItems: RouterInfo,
  ctx,
) => {
  // Start with a clean items url.
  if (!process.browser || Object.keys(newQueryString).length === 0) {
    // When there is a server side reload we want to clear any state but also
    // if its a clean redirect (client side) to the page without any filters.
    clearSearchUrlItems();
  }

  if (routeQuerystringItems.facets) {
    if (ctx.req) {
      // Store information about URL clean items to be available to the client who needs
      // the information about these fiters and what they are about (e.g. /full-time/enginnering)
      // this will say that full time is an employment type (and which id) and that engineering is an occupation.
      setBrowserCookie(
        'cleanUrlitems',
        JSON.stringify({
          ...Object.keys(routeQuerystringItems.facets).map((key) => {
            return JSON.stringify({
              key: routeQuerystringItems.facets[key].facetId,
              value: routeQuerystringItems.facets[key].facetKey,
              alias: routeQuerystringItems.facets[key].path,
            });
          }),
          ...(routeQuerystringItems.geoLocation && {
            key: 'geo_location',
            value: generateQueryStringURL(routeQuerystringItems.geoLocation),
            alias: routeQuerystringItems.geoLocation.path,
          }),
        }),
        {},
      );
    }
  }
};

/**
 * @param query
 *  The current querystring object.
 *
 * @param routeInfo
 *  The routeInfo is the route information received from the backend.
 */
export const getSearchQueryStringObject = (
  query,
  routeInfo: SearchResponse,
  ctx,
): {
  urlItemsToAdd: URLfilter[];
  newQueryString: ParsedUrlQuery;
} => {
  let urlItemsToAdd: URLfilter[] = [];
  let newQueryString = { ...query };
  const routeQuerystringItems: RouterInfo = getRouteQuerystring(routeInfo);

  // Set important information about filters like alias.
  setCookieFilterInformation(newQueryString, routeQuerystringItems, ctx);

  // Set Geolocation items.
  if (routeQuerystringItems.geoLocation) {
    const geoLocationQueryParamsObject = getGeoLocationUrlObjectFromGeoInput(
      routeQuerystringItems.geoLocation as any,
    );
    const queryParamsString = generateQueryStringURL(
      geoLocationQueryParamsObject,
    );

    newQueryString = {
      ...newQueryString,
      ...routeQuerystringItems.geoLocation,
    };
    urlItemsToAdd = [
      ...urlItemsToAdd,
      {
        key: 'geo_location',
        value: queryParamsString,
        alias: routeInfo?.geoLocation?.path ?? '',
      },
    ];
  }

  if (routeQuerystringItems.facets) {
    Object.keys(routeQuerystringItems.facets).map((key) => {
      // Add to the current items in search url.
      urlItemsToAdd = [
        ...urlItemsToAdd,
        {
          key: routeQuerystringItems.facets[key].facetId,
          value: routeQuerystringItems.facets[key].facetKey,
          alias: routeQuerystringItems.facets[key].path,
        },
      ];

      let newQSArray: any[] = [];

      /**
       * This will handle making sure that nextjs still knows about the querystring in the
       * format it is used to (an object with keys and values and where multiple keys are arrays).
       *
       * We are basically builidng the new querystring oject with the clean url facets as well even though
       * they are not "visible" in the URL querystring.
       */
      if (
        Array.isArray(newQueryString[routeQuerystringItems.facets[key].facetId])
      ) {
        newQSArray = [
          ...newQueryString[routeQuerystringItems.facets[key].facetId],
          routeQuerystringItems.facets[key].facetKey,
        ];
        // Remove duplicates
        newQueryString[routeQuerystringItems.facets[key].facetId] = Array.from(
          new Set(newQSArray),
        );
      } else {
        // If the querystring item already has things inside add to it,
        // otherwise set it.
        if (newQueryString[routeQuerystringItems.facets[key].facetId]) {
          newQSArray = newQueryString[
            routeQuerystringItems.facets[key].facetId
          ].split(',');
          newQSArray = [
            ...newQSArray,
            routeQuerystringItems.facets[key].facetKey,
          ];
          // Remove duplicates
          newQueryString[
            routeQuerystringItems.facets[key].facetId
          ] = Array.from(new Set(newQSArray));
        } else {
          newQueryString[routeQuerystringItems.facets[key].facetId] =
            routeQuerystringItems.facets[key].facetKey;
        }
      }
    });
  }

  /**
   * @todo MAKE THIS BETTER.
   */
  delete newQueryString.taxonomy;
  delete newQueryString.router;

  return { urlItemsToAdd, newQueryString };
};

export const removeRouteInfoFromURL = (
  url: string,
  routeInfo: SearchResponse,
  query,
): string => {
  delete query.taxonomy;
  delete query.router;

  routeInfo.facets?.map((facet) => {
    if (
      facet &&
      facet?.facetId &&
      (facet.facetKey === query[facet?.facetId] ||
        query[facet?.facetId]?.includes(facet.facetKey))
    ) {
      // Remove from query
      // it can be a single value or array.

      if (Array.isArray(query[facet.facetId])) {
        query[facet.facetId] = query[facet.facetId].filter(
          (i) => i !== facet?.facetKey,
        );
      } else {
        delete query[facet.facetId];
      }
    }
  });

  const queryUrl = generateQueryStringURL(query);

  return `${getCleanUrlPart(url)}${queryUrl ? `?${queryUrl}` : ''}`;
};

/**
 * Concatenates values by field in a given facet array with provided concatenator.
 *
 * @param {Facet[]} facets
 *  Facets with values for the concatenation.
 * @param {string} field
 *  Facet field to return labels for.
 * @param {string} concatenator
 *  Concatenator symbol.
 * @returns {string}
 *  Concatenated facet values labels if facet with provided value exists.
 */
export const concatenateSearchFacetLabels = (
  facets: Facet[],
  field: string,
  concatenator = ' | ',
): string | undefined =>
  facets
    .filter((facet) => facet.name === field)
    .map((facet) => facet.values.map((value) => value.label))
    .join(concatenator);
